import { useEffect, useId, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { RaadLogo } from '@/components/RaadLogo';
import { LangToggle } from '@/components/LangToggle';
import { useAuth } from './useAuth';
import { InvalidCredentialsError } from './authClient';
import { ApiError } from '@/lib/apiClient';
import { WHATSAPP_URL } from '@/lib/contact';
import { COUNTRIES, DEFAULT_COUNTRY_ISO, countryByIso } from './countries';

/** The three states this form can surface, each its own i18n message. */
type LoginError = 'invalidCredentials' | 'invalidPhone' | 'network';

/** Route illustration behind the hero (ported from canvas 1h). Decorative. */
function HeroBackdrop() {
  return (
    <svg
      viewBox="0 0 800 800"
      className="pointer-events-none absolute inset-0 h-full w-full opacity-50"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
    >
      <path d="M-50 620 C150 560 260 640 400 520 S650 300 850 240" stroke="var(--raad-hero-road)" strokeWidth="10" fill="none" />
      <path d="M-50 620 C150 560 260 640 400 520 S650 300 850 240" stroke="var(--raad-primary)" strokeWidth="2.5" fill="none" strokeDasharray="8 12" opacity="0.8" />
      <path d="M100 -50 C140 150 60 350 220 520 S300 700 280 850" stroke="var(--raad-hero-road)" strokeWidth="7" fill="none" />
      <path d="M-50 300 L850 340" stroke="var(--raad-hero-ink)" strokeWidth="5" />
      <circle cx="400" cy="520" r="26" fill="var(--raad-primary)" opacity="0.12" />
      <circle cx="400" cy="520" r="9" fill="var(--raad-accent)" />
      <circle cx="620" cy="340" r="7" fill="var(--raad-warn)" />
      <circle cx="180" cy="580" r="7" fill="var(--raad-hero-rule)" />
    </svg>
  );
}

function FeatureDot({ label }: { label: string }) {
  return (
    <span className="flex items-center gap-2">
      <span className="size-1.5 rounded-full bg-raad-accent" />
      {label}
    </span>
  );
}

/**
 * Screen 1h — Login. Built mobile-first: the base layout is a single column for
 * a 360px phone with 48px primary controls; the two-panel marketing split
 * appears only at lg. Backend is phone + password (OTP is a later slice), so the
 * canvas's OTP copy is realised as a password sign-in with the same visual
 * language. Every string resolves through i18n.
 */
export function Login() {
  const { t } = useTranslation();
  const { state, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const phoneId = useId();
  const countryId = useId();
  const passwordId = useId();
  const errorId = useId();

  const [countryIso, setCountryIso] = useState(DEFAULT_COUNTRY_ISO);
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<LoginError | null>(null);

  const from = (location.state as { from?: string } | null)?.from ?? '/';

  // Already signed in (e.g. bootstrap resolved, or a second tab) — leave login.
  // A staff principal navigates away too: the protected route it lands on is
  // gated by RequireAuth, which renders the in-place staff panel (17i). Staying
  // on the login form would strand a valid session with no way forward.
  useEffect(() => {
    if (state.status === 'authenticated' || state.status === 'staff') {
      navigate(from, { replace: true });
    }
  }, [state.status, from, navigate]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      // Compose international form: <selected dial code> + the national digits as
      // typed. raad-api's normalisePhone (libphonenumber-js) collapses it to
      // canonical E.164. Verified against the real dependency: for Somalia this
      // is byte-identical to the old national-only submission — `+252`+`612345678`
      // and `+252`+`0612345678` both normalise to `+252612345678`. If the user
      // pasted their own `+` prefix, respect it and don't double the dial code.
      const typed = phone.trim();
      const wire = typed.startsWith('+')
        ? typed.replace(/\s+/g, '')
        : `+${countryByIso(countryIso).dial}${typed.replace(/\D/g, '')}`;
      await login(wire, password);
      // On success useAuth flips to authenticated and the effect above redirects.
    } catch (err) {
      // Three distinct outcomes, three honest messages — never blame the network
      // for a problem the network didn't cause. The login route validates with
      // zValidator, so a number `normalisePhone` rejects comes back as a 400
      // (ApiError.status === 400), NOT a 401 (which is wrong-credentials →
      // InvalidCredentialsError) and NOT a transport failure (fetch rejects, so
      // no ApiError at all). This path got more reachable the moment other
      // countries became selectable — a wrong-length number is now a live case.
      setError(
        err instanceof InvalidCredentialsError
          ? 'invalidCredentials'
          : err instanceof ApiError && err.status === 400
            ? 'invalidPhone'
            : 'network',
      );
      setSubmitting(false);
    }
  }

  const brand = t('brand.name');

  return (
    <main className="flex min-h-dvh flex-col bg-raad-bg text-raad-text lg:flex-row">
      {/* HERO — compact branded band on mobile, full marketing panel at lg. */}
      <section className="relative flex flex-col justify-between overflow-hidden bg-raad-deep px-5 pb-6 pt-8 sm:px-8 lg:flex-[1.2] lg:px-14 lg:py-12">
        <HeroBackdrop />
        <div className="relative flex items-center gap-3">
          <RaadLogo size={40} title={`${brand} ${t('brand.suffix')}`} />
          <div className="font-display text-xl font-bold sm:text-2xl">
            {brand} <span className="text-raad-accent">{t('brand.suffix')}</span>
          </div>
        </div>

        <div className="relative mt-6 lg:mt-0">
          <h1 className="max-w-[520px] font-display text-2xl font-bold leading-tight sm:text-3xl lg:text-[44px]">
            {t('login.hero.headline')}
          </h1>
          <p className="mt-3 hidden max-w-[460px] text-[15px] leading-relaxed text-raad-muted lg:mt-4 lg:block">
            {t('login.hero.subtitle')}
          </p>
          <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-xs font-medium text-raad-muted lg:mt-7">
            <FeatureDot label={t('login.hero.feature.live')} />
            <FeatureDot label={t('login.hero.feature.billing')} />
            <FeatureDot label={t('login.hero.feature.languages')} />
          </div>
        </div>

        <p className="relative mt-6 hidden text-xs text-raad-faint lg:block">
          {t('login.hero.footer', { brand })}
        </p>
      </section>

      {/* FORM */}
      <section className="flex flex-1 items-start justify-center px-5 py-8 sm:items-center sm:px-8 lg:py-12">
        <div className="w-full max-w-[420px]">
          <div className="mb-6 flex justify-end lg:mb-7">
            <LangToggle />
          </div>

          <h2 className="font-display text-2xl font-bold sm:text-[28px]">{t('login.title')}</h2>
          <p className="mt-2 text-sm text-raad-muted">{t('login.subtitle')}</p>

          <form onSubmit={onSubmit} noValidate className="mt-7">
            <label
              htmlFor={phoneId}
              className="mb-2 block text-xs font-semibold uppercase tracking-[0.08em] text-raad-muted"
            >
              {t('login.phone.label')}
            </label>
            <div className="flex gap-2.5">
              {/* Native <select>: the most reliable country picker on a 360px
                  Android screen — the OS renders a full-screen wheel, it is
                  keyboard-operable for free (focus, type-ahead, arrows, Enter),
                  and it needs no JS to open. A custom combobox would risk both
                  the keyboard and the small-screen cases the brief calls out. */}
              <label htmlFor={countryId} className="sr-only">
                {t('login.phone.country')}
              </label>
              <select
                id={countryId}
                value={countryIso}
                onChange={(e) => setCountryIso(e.target.value)}
                dir="ltr"
                className="min-h-12 w-[104px] shrink-0 rounded-xl border border-raad-line-10 bg-raad-input px-2.5 text-[15px] font-semibold focus:border-raad-accent focus:outline-none"
              >
                {COUNTRIES.map((c) => (
                  <option key={c.iso} value={c.iso}>
                    {c.flag} +{c.dial} · {t(`login.country.${c.iso}`)}
                  </option>
                ))}
              </select>
              <input
                id={phoneId}
                type="tel"
                inputMode="tel"
                autoComplete="tel-national"
                dir="ltr"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                aria-invalid={error != null}
                aria-describedby={error ? errorId : undefined}
                className="min-h-12 flex-1 rounded-xl border border-raad-line-10 bg-raad-input px-4 text-base font-semibold placeholder:font-normal placeholder:text-raad-dim focus:border-raad-accent focus:outline-none"
              />
            </div>

            <label
              htmlFor={passwordId}
              className="mb-2 mt-5 block text-xs font-semibold uppercase tracking-[0.08em] text-raad-muted"
            >
              {t('login.password.label')}
            </label>
            <input
              id={passwordId}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('login.password.placeholder')}
              aria-invalid={error != null}
              aria-describedby={error ? errorId : undefined}
              className="min-h-12 w-full rounded-xl border border-raad-line-10 bg-raad-input px-4 text-base font-semibold placeholder:font-normal placeholder:text-raad-dim focus:border-raad-accent focus:outline-none"
            />

            {error && (
              <p id={errorId} role="alert" className="mt-4 text-sm font-medium text-raad-error">
                {t(`login.error.${error}`)}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="mt-6 min-h-12 w-full rounded-xl bg-raad-primary px-4 text-[15px] font-bold text-raad-onprimary transition-opacity hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? t('login.submitting') : t('login.submit')}
            </button>
          </form>

          <div className="my-6 flex items-center gap-3">
            <span className="h-px flex-1 bg-raad-fill-8" />
            <span className="text-xs text-raad-dim">{t('login.dividerNew', { brand })}</span>
            <span className="h-px flex-1 bg-raad-fill-8" />
          </div>

          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noreferrer"
            className="flex min-h-12 w-full items-center justify-center rounded-xl border border-raad-line-14 px-4 text-sm font-semibold text-raad-text transition-colors hover:border-raad-line-25"
          >
            {t('login.whatsapp')}
          </a>

          <p className="mt-6 text-center text-xs text-raad-dim">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noreferrer"
              className="underline decoration-raad-line-20 underline-offset-2 hover:text-raad-text hover:decoration-raad-line-40"
            >
              {t('login.help')}
            </a>
          </p>
        </div>
      </section>
    </main>
  );
}
